import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { useReminders, Reminder } from '../../hooks/useReminders';
import { Bell, Plus, Check, Clock, Trash2, ChevronDown } from 'lucide-react';
export function RemindersWidget() {
  const {
    reminders,
    addReminder,
    dismissReminder,
    snoozeReminder,
    removeReminder
  } = useReminders();
  const [showAdd, setShowAdd] = useState(false);
  const [newText, setNewText] = useState('');
  const [newTime, setNewTime] = useState('');
  const activeReminders = reminders.filter(r => !r.dismissed).sort((a, b) => new Date(a.snoozedUntil || a.datetime).getTime() - new Date(b.snoozedUntil || b.datetime).getTime());
  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newText || !newTime) return;
    const dt = new Date();
    const [hours, minutes] = newTime.split(':');
    dt.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    // If time has passed today, set for tomorrow
    if (dt.getTime() < Date.now()) {
      dt.setDate(dt.getDate() + 1);
    }
    addReminder({
      text: newText,
      datetime: dt.toISOString()
    });
    setNewText('');
    setNewTime('');
    setShowAdd(false);
  };
  const formatRelativeTime = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMins / 60);
    const diffDays = Math.round(diffHours / 24);
    if (diffMins < 0) return 'Atrasado';
    if (diffMins < 60) return `en ${diffMins} min`;
    if (diffHours < 24) return `en ${diffHours} hs`;
    if (diffDays === 1) return 'mañana';
    return `en ${diffDays} días`;
  };
  const isOverdue = (isoString: string) => new Date(isoString).getTime() < Date.now();
  return <Card className="h-full bg-slate-900 border-slate-800 flex flex-col max-h-[400px]" data-id="element-3235">
      <div className="p-4 border-b border-slate-800 flex items-center justify-between shrink-0" data-id="element-3236">
        <h3 className="text-sm font-semibold text-slate-100 flex items-center gap-2" data-id="element-3237">
          <Bell size={16} className="text-rose-400" data-id="element-3238" />
          Recordatorios
        </h3>
        <button onClick={() => setShowAdd(!showAdd)} className="p-1 text-slate-400 hover:text-rose-400 transition-colors" data-id="element-3239">
          <Plus size={16} data-id="element-3240" />
        </button>
      </div>

      {showAdd && <div className="p-3 border-b border-slate-800 bg-slate-950/50" data-id="element-3241">
          <form onSubmit={handleAdd} className="space-y-2" data-id="element-3242">
            <input type="text" value={newText} onChange={e => setNewText(e.target.value)} placeholder="Recordarme..." className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-rose-500" autoFocus data-id="element-3243" />

            <div className="flex gap-2" data-id="element-3244">
              <input type="time" value={newTime} onChange={e => setNewTime(e.target.value)} className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-rose-500" data-id="element-3245" />

              <button type="submit" disabled={!newText || !newTime} className="px-3 py-1.5 bg-rose-600 text-white rounded-lg hover:bg-rose-500 disabled:opacity-50 text-sm font-medium" data-id="element-3246">
                Guardar
              </button>
            </div>
          </form>
        </div>}

      <div className="flex-1 overflow-y-auto p-2 custom-scrollbar" data-id="element-3247">
        {activeReminders.length === 0 ? <div className="text-center py-8 text-slate-500 text-xs" data-id="element-3248">
            No hay recordatorios activos
          </div> : <div className="space-y-2" data-id="element-3249">
            {activeReminders.map(reminder => {
          const targetTime = reminder.snoozedUntil || reminder.datetime;
          const overdue = isOverdue(targetTime);
          return <div key={reminder.id} className={`group flex items-start gap-3 p-3 rounded-xl border transition-all ${overdue ? 'bg-rose-950/20 border-rose-900/30' : 'bg-slate-950 border-slate-800'}`} data-id="element-3250">
                  <button onClick={() => dismissReminder(reminder.id)} className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors ${overdue ? 'border-rose-500 hover:bg-rose-500 hover:text-white' : 'border-slate-600 hover:border-emerald-500 hover:bg-emerald-500 hover:text-white'}`} data-id="element-3251">
                    <Check size={10} className="opacity-0 hover:opacity-100" data-id="element-3252" />
                  </button>

                  <div className="flex-1 min-w-0" data-id="element-3253">
                    <p className={`text-sm font-medium truncate ${overdue ? 'text-rose-200' : 'text-slate-200'}`} data-id="element-3254">
                      {reminder.text}
                    </p>
                    <p className={`text-xs mt-0.5 ${overdue ? 'text-rose-400 font-semibold' : 'text-slate-500'}`} data-id="element-3255">
                      {formatRelativeTime(targetTime)}
                      {reminder.recurring && <span className="ml-2 text-[10px] bg-slate-800 px-1.5 py-0.5 rounded" data-id="element-3256">
                          ↻ {reminder.recurring}
                        </span>}
                    </p>
                  </div>

                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3257">
                    <div className="relative group/snooze" data-id="element-3258">
                      <button className="p-1.5 text-slate-400 hover:text-amber-400 rounded-md hover:bg-slate-800" data-id="element-3259">
                        <Clock size={14} data-id="element-3260" />
                      </button>
                      <div className="absolute right-0 top-full mt-1 w-32 bg-slate-800 border border-slate-700 rounded-lg shadow-xl opacity-0 invisible group-hover/snooze:opacity-100 group-hover/snooze:visible transition-all z-10 py-1" data-id="element-3261">
                        <button onClick={() => snoozeReminder(reminder.id, 15)} className="w-full text-left px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700" data-id="element-3262">
                          15 minutos
                        </button>
                        <button onClick={() => snoozeReminder(reminder.id, 60)} className="w-full text-left px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700" data-id="element-3263">
                          1 hora
                        </button>
                        <button onClick={() => snoozeReminder(reminder.id, 1440)} className="w-full text-left px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-700" data-id="element-3264">
                          Mañana
                        </button>
                      </div>
                    </div>
                    <button onClick={() => removeReminder(reminder.id)} className="p-1.5 text-slate-400 hover:text-red-400 rounded-md hover:bg-slate-800" data-id="element-3265">
                      <Trash2 size={14} data-id="element-3266" />
                    </button>
                  </div>
                </div>;
        })}
          </div>}
      </div>
    </Card>;
}